import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import * as DateHelpers from './DateHelpers';
import { toast } from 'react-toastify';
import type { GenericList } from './models/JobSharedInterfaces';
import { GetQuery, PostQuery } from './Shared';

interface JobScheduleAddPCProps {
    scheduleId: number;
    frequencyOptions: GenericList[];
    quantityTotal: number;
    showModal: boolean;
    hideModal(): void;
    saveModal(): void;
}

const JobScheduleAddPumpCleanPopup = (props: JobScheduleAddPCProps) => {
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [frequencyId, setFrequencyId] = React.useState(-1);
    const [scheduleDate, setScheduleDate] = React.useState("");
    const [notes, setNotes] = React.useState("");
    const [invoicePcRemaining, setInvoicePcRemaining] = React.useState(0);
    const [invoicePcExtra, setInvoicePcExtra] = React.useState(0);
    const [invoiceAmount, setInvoiceAmount] = React.useState(0);
    const [invoiceAmountFormatted, setInvoiceAmountFormatted] = React.useState("");

    const handleFrequencyChange = (e: any) => {
        clearError(e);
        var freqId = e.target.value * 1
        setFrequencyId(freqId);

        var validDate = scheduleDate ? DateHelpers.isValidDate(scheduleDate) : false;

        if (freqId > 0 && validDate) {
            calculateInitialInvoice(scheduleDate, freqId);
        } else {
            setInvoicePcRemaining(0);
            setInvoicePcExtra(0);
            setInvoiceAmount(0);
            setInvoiceAmountFormatted("");
        }
    }

    const handleScheduleDateChange = (e: any) => {
        clearError(e);

        setScheduleDate(e.target.value);

        var validDate = e.target.value ? DateHelpers.isValidDate(e.target.value) : false;

        if (frequencyId > 0 && validDate) {
            calculateInitialInvoice(e.target.value, frequencyId);
        } else {
            setInvoicePcRemaining(0);
            setInvoicePcExtra(0);
            setInvoiceAmount(0);
            setInvoiceAmountFormatted("");
        }
    }

    const clearError = (e: any) => {
        var error = errors;
        if (e.target.value) {
            delete error[e.target.name];
            setErrors(error);
        }
    }

    const hideModal = () => {
        setFrequencyId(-1);
        setScheduleDate("");
        setInvoicePcRemaining(0);
        setInvoicePcExtra(0);
        setInvoiceAmount(0);
        setInvoiceAmountFormatted("");
        props.hideModal();
    }

    const calculateInitialInvoice = async (pcDate: string, frequencyId: number) => {
        const apiUrl = 'GetAddPumpAndCleanInvoice?ScheduleId=' + props.scheduleId + '&PumpCleanDate=' + pcDate + "&FrequencyId=" + frequencyId + "&QuantityTotal=" + props.quantityTotal;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setInvoicePcRemaining(result.data.pumpAndCleanRemaining);
            setInvoicePcExtra(result.data.pumpAndCleanExtra);
            setInvoiceAmount(result.data.amount);
            setInvoiceAmountFormatted(result.data.amountFormatted);
        }
    }

    const validate = () => {
        let error: any = {};
        let formIsValid = true;

        if (frequencyId <= 0) {
            formIsValid = false;
            error["frequencyId"] = "Frequency is required!";
        }

        if (!scheduleDate) {
            formIsValid = false;
            error["scheduleDate"] = "Schedule Date is required!";
        } else if (!DateHelpers.isValidDate(scheduleDate)) {
            formIsValid = false;
            error["scheduleDate"] = "Valid Schedule Date is required!";
        } else if (DateHelpers.isDateInPast(scheduleDate)) {
            formIsValid = false;
            error["scheduleDate"] = "Schedule Date must be today or later!";
        }

        setErrors(error);

        return formIsValid;
    }

    const savePumpAndClean = () => {
        if (validate()) {
            saveSchedulePumpAndClean();
        } else {
            toast.error("Fix errors on screen before saving!");
        }
    }

    const saveSchedulePumpAndClean = async () => {
        var scheduleJob = {
            scheduleId: props.scheduleId,
            frequencyId: frequencyId,
            scheduleDate: scheduleDate,
            pcRemaining: invoicePcRemaining,
            pcExtra: invoicePcExtra,
            invoiceAmount: invoiceAmount,
            notes: notes
        };

        var jsonItem = JSON.stringify(scheduleJob);

        const apiUrl = "SavePumpAndCleanToToilet";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("Pump and Clean saved successfully");
            setFrequencyId(-1);
            setScheduleDate("");
            setInvoicePcRemaining(0);
            setInvoicePcExtra(0);
            setInvoiceAmount(0);
            setInvoiceAmountFormatted("");
            props.saveModal();
        }
    }

    return (
        <Modal show={props.showModal} onHide={hideModal}>
            <Modal.Header className="modal__header--colour">
                <Modal.Title>Add Pump & Clean Maintenance to Toilet</Modal.Title>
                <button type="button" className="close modal__close--colour" onClick={hideModal}>&times;</button>
            </Modal.Header>
            <Modal.Body>
                <div className="errors">NOTE: This will add a $0 Pump & Clean to the Toilet</div>
                <label className="input-group" htmlFor="frequencyId">
                    <span className="label">Frequency</span>
                    <select className="select" id="frequencyId" name="frequencyId" value={frequencyId} onChange={(e) => handleFrequencyChange(e)}>
                        <option defaultValue="-1"></option>
                        {props.frequencyOptions.filter(f => f.id < 4).map(item =>
                            <option key={item.id} value={item.id}>{item.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["frequencyId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["frequencyId"]}</span>

                <label className="input-group" htmlFor="scheduleDate">
                    <span className="label">Date</span>
                    <input className="input" type="date" id="scheduleDate" name="scheduleDate" value={scheduleDate} onChange={(e) => handleScheduleDateChange(e)}></input>
                </label>
                <span className={errors["scheduleDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["scheduleDate"]}</span>

                <label className="input-group" htmlFor="notes">
                    <span className="label-large">Notes (optional)</span>
                    <textarea className="input allocate__teamnotes" id="notes" name="notes" value={notes} onChange={(e) => setNotes(e.target.value)}></textarea>
                </label>

                <div className={invoiceAmount > 0 ? "marginTop10" : "hidden"}>
                    <span>An Invoice will be created for the extra {invoicePcExtra} pump & cleans within the initial hire period for {invoiceAmountFormatted}</span>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="popup-button--yes" onClick={savePumpAndClean}>Save</button>
            </Modal.Footer>
        </Modal>
    )
}

export default JobScheduleAddPumpCleanPopup;